import { useEffect, useState } from 'react';
import css from './index.module.scss';
import { Button, Divider, IconNode, ToolTip, Typography } from '@components/base';
import Images from '@assets/images';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import QuoteProductDetails from '../../../quote-product-details';
import { createFilterOptions } from '@mui/material';
import QuoteZipcodeDischarge from '../zipcode-placeofdischarge';
import DomesticQuotePrices from '../domestic-quote-prices';
import DomesticQuotePercentage from '../domestic-quote-percentages';
import {
  calculateCMValues,
  EMPTY_CM_VALUES,
  ICMValues,
  calculateDomesticFinalQuoteSellingPrice,
  calculateDomesticQuoteCost,
  convertPricePerUnit,
  convertWeight,
  roundTo2
} from '@helpers/utils';
import QuotePricePackageWrapper from '../../../quote-price-package-wrapper';
import { TextField } from '@components/common';
import QuoteCountryField from '@components/common/quote-wrapper/components/quote-country-field';

const filter = createFilterOptions<any>();

interface INonConsolidationDomesticProps {
  isEnquiry?: boolean;
  index: number;
  handleDuplicateQuote?: (index: number) => void;
  handleDeleteClick?: () => void;
  productOptions: any[];
  filterProductOptions: (event: any, param?: any) => Promise<void>;
  fieldNames: any;
  fieldLength: number;
  selectedWeightSystemChildOptions: any[];
  selectedCurrency: any[];
  primaryUnitWatch: any;
  currency1Watch: any;
  disableProductName?: boolean;
  enableZipcodeAndPlaceOfDischarge?: boolean;
}

const NonConsolidationDomestic = (props: INonConsolidationDomesticProps) => {
  const {
    isEnquiry = true,
    index,
    handleDuplicateQuote,
    handleDeleteClick,
    productOptions,
    filterProductOptions,
    fieldNames,
    fieldLength,
    selectedWeightSystemChildOptions,
    selectedCurrency,
    primaryUnitWatch,
    currency1Watch,
    disableProductName = false,
    enableZipcodeAndPlaceOfDischarge = false
  } = props;

  const {
    isExpanded,
    productName,
    productPackaging,
    dealQuantity,
    dealQuantityUnit,
    moq,
    moqUnit,
    incoTermsZipCode,
    incoTermsLocation,
    countryOfOrigin,
    materialLocationCountry,
    totalWeight,
    totalWeightUnit,
    totalLocalFreightPrice,
    totalLocalFreightCurrency,
    basePrice,
    basePriceCurrency,
    basePriceWeightUnit,
    warehousingCostPrice,
    warehousingCostCurrency,
    additionalCostPrice,
    additionalCostCurrency,
    financeCost,
    profitMarginExpected,
    discount,
    insuranceCost,
    finalQuoteSellingPrice,
    finalQuoteSellingPriceCurrency,
    finalQuoteSellingPriceWeightUnit,
    finalQuoteSellingPriceManualOverride,
    basePriceManualOverride
  } = fieldNames;

  const { control, setValue } = useFormContext();
  const [cmValues, setCmValues] = useState<ICMValues>(EMPTY_CM_VALUES);
  // Not part of the payload; kept in form state so the cost calculator can show CM1 / CM2
  const cmValuesField = profitMarginExpected.replace(/profitMarginExpected$/, 'cmValues');

  const isExpandedWatch = useWatch({
    control,
    name: isExpanded
  });

  const zipcodeWatch = useWatch({
    control,
    name: incoTermsZipCode
  });

  const finalQuoteSellingPriceManualOverrideWatch = useWatch({
    control,
    name: finalQuoteSellingPriceManualOverride
  });

  const basePriceManualOverrideWatch = useWatch({
    control,
    name: basePriceManualOverride
  });

  const finalQuoteSellingPriceWatch = useWatch({
    control,
    name: finalQuoteSellingPrice
  });

  const finalQuoteSellingPriceCurrencyWatch = useWatch({
    control,
    name: finalQuoteSellingPriceCurrency
  });

  const finalQuoteSellingPriceWeightUnitWatch = useWatch({
    control,
    name: finalQuoteSellingPriceWeightUnit
  });

  const basePriceWeightUnitWatch = useWatch({
    control,
    name: basePriceWeightUnit
  });

  const basePriceWatch = useWatch({
    control,
    name: basePrice
  });

  const basePriceCurrencyWatch = useWatch({
    control,
    name: basePriceCurrency
  });

  const warehousePriceWatch = useWatch({
    control,
    name: warehousingCostPrice
  });

  const warehousingCostCurrencyWatch = useWatch({
    control,
    name: warehousingCostCurrency
  });

  const totalWeightWatch = useWatch({
    control,
    name: totalWeight
  });

  const totalWeightUnitWatch = useWatch({
    control,
    name: totalWeightUnit
  });

  const totalLocalFreightPriceWatch = useWatch({
    control,
    name: totalLocalFreightPrice
  });

  const totalLocalFreightCurrencyWatch = useWatch({
    control,
    name: totalLocalFreightCurrency
  });

  const additionalCostPriceWatch = useWatch({
    control,
    name: additionalCostPrice
  });

  const additionalCostCurrencyWatch = useWatch({
    control,
    name: additionalCostCurrency
  });

  const financeCostWatch = useWatch({
    control,
    name: financeCost
  });
  const profitMarginExpectedWatch = useWatch({
    control,
    name: profitMarginExpected
  });
  const discountWatch = useWatch({
    control,
    name: discount
  });
  const insuranceCostWatch = useWatch({
    control,
    name: insuranceCost
  });
  const dealQuantityWatch = useWatch({
    control,
    name: dealQuantity
  });
  const dealQuantityUnitWatch = useWatch({
    control,
    name: dealQuantityUnit
  });

  const handleProductSelection = (event: any, param: any) => {
    if (param) {
      setValue(productName, param);
    } else {
      setValue(productName, null);
    }
  };

  // Calculate final domestic price
  useEffect(() => {
    const dealQuantityInPrimaryUnit = convertWeight(
      dealQuantityWatch || 0,
      dealQuantityUnitWatch?.value,
      primaryUnitWatch?.value
    );

    const totalWeightInPrimaryUnit = convertWeight(
      totalWeightWatch || 0,
      totalWeightUnitWatch?.value,
      primaryUnitWatch?.value
    );

    const weightToUse =
      dealQuantityInPrimaryUnit == 0
        ? totalWeightInPrimaryUnit
        : totalWeightInPrimaryUnit == 0
        ? dealQuantityInPrimaryUnit
        : Math.min(dealQuantityInPrimaryUnit, totalWeightInPrimaryUnit);

    const finalDomesticPrice = calculateDomesticFinalQuoteSellingPrice(
      basePriceWatch,
      basePriceWeightUnitWatch,
      weightToUse,
      insuranceCostWatch || 0,
      discountWatch || 0,
      profitMarginExpectedWatch || 0,
      financeCostWatch || 0,
      additionalCostPriceWatch,
      totalLocalFreightPriceWatch,
      warehousePriceWatch,
      finalQuoteSellingPriceWeightUnitWatch,
      finalQuoteSellingPriceCurrencyWatch,
      primaryUnitWatch
    );

    setValue(finalQuoteSellingPrice, finalDomesticPrice);

    const quoteCost = calculateDomesticQuoteCost(
      basePriceWatch,
      basePriceWeightUnitWatch,
      weightToUse,
      insuranceCostWatch || 0,
      additionalCostPriceWatch,
      totalLocalFreightPriceWatch,
      warehousePriceWatch,
      primaryUnitWatch
    );
    const quoteCMValues = calculateCMValues(
      quoteCost,
      // SP as quoted: rounded to 2 decimals in the selling price unit, then converted
      convertPricePerUnit(
        roundTo2(finalDomesticPrice),
        finalQuoteSellingPriceWeightUnitWatch?.value,
        primaryUnitWatch?.value
      ),
      financeCostWatch,
      dealQuantityInPrimaryUnit
    );
    setCmValues(quoteCMValues);
    setValue(cmValuesField, quoteCMValues);
  }, [
    dealQuantityUnitWatch,
    dealQuantityWatch,
    insuranceCostWatch,
    discountWatch,
    profitMarginExpectedWatch,
    financeCostWatch,
    additionalCostPriceWatch,
    additionalCostCurrencyWatch,
    totalLocalFreightPriceWatch,
    totalLocalFreightCurrencyWatch,
    totalWeightWatch,
    totalWeightUnitWatch,
    warehousePriceWatch,
    warehousingCostCurrencyWatch,
    basePriceWatch,
    basePriceCurrencyWatch,
    basePriceWeightUnitWatch,
    finalQuoteSellingPriceWatch,
    finalQuoteSellingPriceCurrencyWatch,
    finalQuoteSellingPriceWeightUnitWatch,
    primaryUnitWatch
  ]);

  return (
    <main className={css.mainWrapper}>
      {isEnquiry ? (
        <div className={isExpandedWatch ? css.headerWrapper : css.headerWrapperCollapsed}>
          <div className={css.headerText}>Product {index + 1}</div>
          <div className={css.headerIcons}>
            {handleDuplicateQuote && (
              <Button variant="text" onClick={() => handleDuplicateQuote(index)}>
                Duplicate Product
              </Button>
            )}
            {handleDeleteClick && (
              <ToolTip title="Delete">
                <div>
                  <IconNode
                    src={fieldLength > 1 ? Images.deleteRed : Images.delete}
                    onClick={fieldLength > 1 ? handleDeleteClick : undefined}
                  />
                </div>
              </ToolTip>
            )}
            <ToolTip title={isExpandedWatch ? 'Collapse' : 'Expand'}>
              <div>
                <IconNode
                  src={isExpandedWatch ? Images.arrowUpBig : Images.arrowDownBig}
                  onClick={() => setValue(isExpanded, !isExpandedWatch)}
                />
              </div>
            </ToolTip>
          </div>
        </div>
      ) : null}

      {isExpandedWatch && (
        <>
          <div className={css.contentWrapper}>
            <QuoteProductDetails
              productName={productName}
              productOptions={productOptions}
              filterProductOptions={filterProductOptions}
              handleProductSelection={handleProductSelection}
              filter={filter}
              dealQuantity={dealQuantity}
              dealQuantityUnit={dealQuantityUnit}
              selectedWeightSystemChildOptions={selectedWeightSystemChildOptions}
              productPackaging={productPackaging}
              moq={moq}
              moqUnit={moqUnit}
              productNameDisable={disableProductName}
              basePriceManualOverride={basePriceManualOverrideWatch}
              finalQuoteSellingPriceManualOverride={finalQuoteSellingPriceManualOverrideWatch}
            />
          </div>

          {enableZipcodeAndPlaceOfDischarge && (
            <QuoteZipcodeDischarge
              incoTermsZipCode={incoTermsZipCode}
              incoTermsLocation={incoTermsLocation}
              zipcodeWatch={zipcodeWatch}
            />
          )}

          <div className={css.rowWrapper}>
            <QuoteCountryField name={countryOfOrigin} label="Country of Origin" />
            <QuoteCountryField name={materialLocationCountry} label="Material Location Country" />
          </div>

          <Divider className={css.divider} />

          <DomesticQuotePrices
            totalWeight={totalWeight}
            totalWeightUnit={totalWeightUnit}
            totalLocalFreightPrice={totalLocalFreightPrice}
            totalLocalFreightCurrency={totalLocalFreightCurrency}
            basePrice={basePrice}
            basePriceCurrency={basePriceCurrency}
            basePriceWeightUnit={basePriceWeightUnit}
            warehousingCostPrice={warehousingCostPrice}
            warehousingCostCurrency={warehousingCostCurrency}
            selectedWeightSystemChildOptions={selectedWeightSystemChildOptions}
            selectedCurrency={selectedCurrency}
            basePriceWeightUnitWatch={basePriceWeightUnitWatch}
          />
          <div className={css.rowWrapperHalf}>
            <QuotePricePackageWrapper
              label={
                <span>
                  Additional Cost
                  {primaryUnitWatch && (
                    <Typography variant="label"> ( Per {primaryUnitWatch?.label} )</Typography>
                  )}
                </span>
              }
              currencyPlaceholder="Currency"
              inputPlaceholder="Enter Price"
              input={additionalCostPrice}
              currency={additionalCostCurrency}
              control={control}
              currencyOptions={selectedCurrency}
            />
          </div>
          <Divider className={css.divider} />

          <DomesticQuotePercentage
            financeCost={financeCost}
            profitMarginExpected={profitMarginExpected}
            discount={discount}
            insuranceCost={insuranceCost}
            cmValues={cmValues}
            cm2Currency={currency1Watch}
          />
        </>
      )}
    </main>
  );
};

export default NonConsolidationDomestic;
